/**
 * USINA — usina térmica desativada, meio-dia sob teto de nuvem.
 *
 * A leitura do mapa em uma frase: a CASA DE MÁQUINAS a noroeste (galpão de aço
 * alto, com a caldeira em cima e passarela interna), a CHAMINÉ ao norte dela
 * como marco visual que se lê de qualquer canto, o PARQUE DE TANQUES a nordeste
 * e leste, o PÁTIO DE TRANSFORMADORES cercado a sudeste, e no meio um pátio
 * aberto de concreto atravessado por tubulação e cortado por um tanque.
 *
 * Convenção: +X leste, +Z sul, Y para cima. Uma unidade = um metro.
 * O contrato completo está no topo de src/world/MapKit.js.
 *
 * ------------------------------------------------------------------------
 * O QUE ESTE MAPA OFERECE QUE OS OUTROS DOIS NÃO OFERECEM
 * ------------------------------------------------------------------------
 * Verticalidade industrial em três níveis (0 / 3,6 / 7,6) e linhas de tiro de
 * 40 m pelo eixo do pátio, cortadas por tanque cilíndrico. Blackout é complexo
 * árido de becos curtos; Porto é cais com labirinto de contêiner. A Usina é a
 * mais aberta em silhueta e a única em que a rota alta atravessa o mapa inteiro.
 *
 * ------------------------------------------------------------------------
 * TRÊS RESTRIÇÕES DO MOTOR QUE MOLDARAM ESTE MAPA
 * ------------------------------------------------------------------------
 * 1. Toda superfície pisável de 4 m² ou mais tem que ser alcançável a pé
 *    (tools/test-rota-alta.mjs). Por isso NENHUM volume deste mapa tem topo
 *    entre 1,9 m (alcance do mantle) e 12 m sem acesso desenhado: ou é baixo o
 *    bastante para escalar, ou é alto demais para o teste tratar como piso
 *    (acima de 12 m), ou tem escada/degrau que chega lá. As bases de 0,9 m sob
 *    as turbinas e de 0,4 m sob os transformadores estão aí por isso: elas
 *    partem o degrau grande em dois pequenos.
 * 2. Passarela e escada precisam de 1,8 m de céu livre em TODO o lance, e piso
 *    de aproximação livre na base. As cinco escadas sobem em céu aberto ou ao
 *    LADO da laje de destino — nunca por baixo dela. Foi o defeito que matou o
 *    Porto três vezes e não aparece em captura de tela nenhuma.
 * 3. `rise` acima de 0,42 m vira parede. Todas as escadas aqui usam 0,267 a
 *    0,286: sobe-se andando, de frente, sem pulo.
 */

// Cotas que aparecem em mais de um lugar. Mudar aqui muda o mapa inteiro junto,
// que é o que impede passarela terminar meio metro acima da laje de destino.
const N1 = 3.6;             // primeiro nível de passarela
const N2 = 7.6;             // segundo nível: laje da casa de máquinas e ponte leste
const CASA = { W: -27.0, E: -9.0, N: -25.0, S: -9.0, H: 7.2, TETO: 7.6 };
// Eixo e espessura do muro de perímetro. A placa tem 0,40 m de propósito: é
// MENOS que o diâmetro da cápsula do jogador (0,70 m), então a crista não é
// superfície em que alguém fica de pé — e com isso ela também deixa de aparecer
// como "ilha inalcançável" na grade de 0,5 m do test-rota-alta, que era o único
// resto de falha do mapa (o Porto ainda tem os dele).
const MURO = 34.5;
const MURO_T = 0.40;
const PERCH = 7.9;          // topo do tanque baixo do leste — 0,30 m acima de N2

export default {
  // O `id` continua `usina` de propósito: é o que vai em `?map=`, o que as ~30
  // ferramentas em `tools/` passam em `--map`, e o que já está escrito nos
  // relatórios desta sessão. Trocar o id renomearia arquivo, testes e links por
  // um ganho que o jogador não vê. O NOME é o que ele lê — e "Caldeira" nomeia
  // a peça que domina a silhueta do mapa, em vez de servir para qualquer
  // instalação industrial do mundo.
  id: 'usina',
  nome: 'Caldeira',
  regiao: 'Térmica desativada',
  seed: 20260815,

  bounds: { min: [-36, -1, -36], max: [36, 30, 36] },

  // Frente é -Z: nasce no portão sul, de costas para a estrada, olhando o pátio
  // com a chaminé no fundo à esquerda.
  player: { spawn: [0, 1.68, 30.0], yaw: 0 },

  // [x, yPé, z, yaw, kind, grounded]
  // `grounded:false` = passarela/laje, que a navgrid (um piso por célula) não
  // cobre. Continuam tendo que ser alcançáveis a pé — test-rota-alta cobra.
  //
  // Cada ponto daqui é conferido pelo Level contra os colisores e DESCARTADO em
  // silêncio se cair dentro de sólido. Duas patrulhas já nasceram assim nesta
  // construção (uma dentro do colisor de um parapeito de sacaria, outra em cima
  // do painel de tela da cerca sul) — ver NOTES-mapa-usina.md.
  spawns: [
    [-17.0, 0, -16.0, 1.5, 'enemy', true],           // piso da casa de máquinas
    [-23.0, 0, -21.0, 0.4, 'enemy', true],           // fundo do vão, junto aos quadros
    [-10.6, N1, -20.0, 3.2, 'sniper', false],        // passarela interna, nível 1
    [-13.0, CASA.TETO, -12.5, 3.4, 'sniper', false], // laje da casa de máquinas
    [2.0, 0, -18.0, 3.0, 'enemy', true],             // pátio norte
    [16.5, 0, -12.0, 4.4, 'enemy', true],            // pé do parque de tanques
    [3.0, N1, -10.9, 1.6, 'sniper', false],          // passarela do pátio, nível 1
    [27.0, PERCH, -4.5, 3.9, 'sniper', false],       // topo do tanque baixo
    [20.8, 3.2, 15.3, 4.0, 'sniper', false],         // laje da casa de comando
    [22.0, 0, 16.0, 4.0, 'enemy', true],             // dentro da casa de comando
    [-24.0, 0, 8.0, 1.6, 'enemy', true],             // área de tratamento, oeste
    [8.0, 0, 24.0, 3.1, 'enemy', true],              // pátio sul
    [-6.0, 0, 12.0, 2.6, 'patrol', true],
    [10.0, 0, 2.0, 3.6, 'patrol', true],
    [-20.0, 0, 24.0, 0.5, 'patrol', true],
    [26.0, 0, -14.0, 4.2, 'patrol', true],
    [-2.0, 0, -28.0, 3.0, 'patrol', true],
    [24.0, 0, 24.0, 3.8, 'patrol', true],
  ],

  // [x, y, z, cor, intensidade, alcance, sombra]
  luzes: [
    [-18.0, 6.4, -17.0, 0xffd2a0, 40, 24, true],   // vão da casa de máquinas
    [-12.0, 3.1, -12.0, 0xffc088, 16, 12, false],  // canto sul do vão
    [-24.0, 3.0, -22.0, 0xffc890, 16, 12, false],  // fundo, junto aos quadros
    [20.8, 2.4, 15.3, 0xd8e4f0, 18, 13, false],    // casa de comando do pátio
    [2.5, 5.4, 30.0, 0xfff0c8, 16, 13, false],     // poste do portão sul
    [17.0, 4.2, -13.0, 0xffcf9a, 18, 14, true],    // poste do parque de tanques
  ],
  // Reator velho de vapor de sódio pisca; luz de escritório não.
  piscam: [0, 5],

  ambiente: {
    // Meio-dia encoberto NÃO é sol a pino: é o céu inteiro virado em fonte de
    // luz. Os dois números que custaram iteração no Porto valem aqui — com o
    // disco a 12° o cosseno na horizontal vale 0,2, então o pátio inteiro
    // depende do CÉU e não do disco: sol 7,0 e hemi 8,5.
    sol: { azimute: 152, elevacao: 12, intensidade: 7.0, cor: 0xffeede },
    // Teto encoberto: o zênite tem que ficar QUASE tão claro quanto o
    // horizonte. É a diferença pequena que lê como nuvem e a grande que lê como
    // fim de tarde. Turbidez baixa pelo mesmo motivo: com o sol baixo, um halo
    // quente forte no horizonte vira pôr do sol na captura.
    ceu: { zenite: 0x98a2a6, horizonte: 0xc2c4c0, chao: 0x928f88, corSol: 0xf2eee6, turbidez: 0.24, neblina: 0.5 },
    // Sob nuvem quase toda a luz vem de cima, difusa e neutra. É o único termo
    // que chega ao piso do pátio.
    hemi: { ceu: 0xbcc2c4, chao: 0xa29e94, intensidade: 7.6 },
    // Névoa da MESMA cor do horizonte: mais escura, o fundo recorta ao contrário.
    fog: { cor: 0xbec2be, densidade: 0.0090 },
    paleta: {
      terreno: 'sand', piso: 'asphalt', parede: 'plaster', estrutura: 'concrete',
      metal: 'metalPainted', ferro: 'gunMetal', madeira: 'woodPlank',
      ferrugem: 'rust', pano: 'fabric',
    },
  },

  build(kit, ctx) {
    const rng = ctx.rng;
    // --- FASE 1: geometria ---------------------------------------------------
    chao(kit, rng);
    perimetro(kit, rng);
    casaDeMaquinas(kit);
    chamine(kit);
    tanques(kit);
    transformadores(kit);
    tubulacao(kit);
    rotaAlta(kit);
    // --- FASE 2: espalhamento (precisa dos colisores já fechados) -------------
    espalhamento(kit, rng);
  },
};

// ---------------------------------------------------------------------------
// chão: laje de concreto da usina sobre terreno de brita
// ---------------------------------------------------------------------------

function chao(kit, rng) {
  kit.groundPlane('asphalt', { size: 180, repeat: 55, name: 'ground' });
  // Praça de concreto no miolo: separa a zona de operação do resto sem linha
  // pintada, e é ela que dá as linhas de tiro longas do mapa.
  kit.deco('concrete', 2.0, 0.012, -2.0, 46, 0.02, 58, { density: 0.45, tint: [0.74, 0.74, 0.73] });
  // juntas de dilatação: é o que dá escala a uma laje de 46 m
  for (let x = -20; x <= 24; x += 6) {
    kit.deco('concrete', x, 0.026, -2.0, 0.12, 0.02, 58, { density: 1.4, tint: [0.72, 0.72, 0.7] });
  }
  for (let z = -30; z <= 26; z += 6) {
    kit.deco('concrete', 2.0, 0.026, z, 46, 0.02, 0.12, { density: 1.4, tint: [0.72, 0.72, 0.7] });
  }
  // brita nas bordas, onde a laje acabou
  for (let i = 0; i < 24; i++) {
    const x = kit.rand(-33, 33), z = kit.rand(-33, 33);
    kit.deco('sand', x, 0.03, z, 2.0 + rng() * 5.0, 0.02, 1.8 + rng() * 4.0,
      { rotY: rng() * 3.14, density: 0.4, tint: [0.98, 0.95, 0.88] });
  }
  // manchas de óleo: escuras, sempre embaixo de alguma coisa que vazou
  for (const [x, z] of [[-14, -12], [16, -20], [20, 14], [-24, 4], [6, -6], [28, -6]]) {
    kit.deco('asphalt', x, 0.028, z, 2.6, 0.02, 2.2, { rotY: 0.4, density: 0.8, tint: [0.5, 0.5, 0.5] });
  }
}

// ---------------------------------------------------------------------------
// perímetro: muro de placa pré-moldada, portão ao sul, brecha a oeste
// ---------------------------------------------------------------------------

function perimetro(kit, rng) {
  const T = MURO_T;
  const lados = [
    // [ax, az, bx, bz, vaos[]]   vão = [u0, u1] deixado sem placa
    [-MURO, -MURO, MURO, -MURO, [[-22.0, -14.0]]],   // norte: sai a tubulação
    [MURO, -MURO, MURO, MURO, []],                   // leste
    [-MURO, -MURO, -MURO, MURO, [[6.0, 12.0]]],      // oeste: placa caída
    [-MURO, MURO, MURO, MURO, [[-4.5, 4.5]]],        // sul: portão principal
  ];
  for (const [ax, az, bx, bz, vaos] of lados) {
    const horiz = Math.abs(bx - ax) >= Math.abs(bz - az);
    const s0 = horiz ? ax : az, s1 = horiz ? bx : bz;
    const fixo = horiz ? az : ax;
    const n = Math.max(1, Math.round((s1 - s0) / 4));
    for (let i = 0; i < n; i++) {
      const a = kit.lerp(s0, s1, i / n), b = kit.lerp(s0, s1, (i + 1) / n);
      if (vaos.some((v) => b > v[0] && a < v[1])) continue;
      const h = 3.4 + rng() * 0.3;
      const tint = [0.80 + rng() * 0.09, 0.80 + rng() * 0.08, 0.77 + rng() * 0.08];
      kit.solid('concrete', horiz ? (a + b) * 0.5 : fixo, h * 0.5, horiz ? fixo : (a + b) * 0.5,
        horiz ? b - a : T, h, horiz ? T : b - a, { tint, uoff: a * 0.5, voff: a * 0.5 });
      kit.deco('concrete', horiz ? (a + b) * 0.5 : fixo, h + 0.08, horiz ? fixo : (a + b) * 0.5,
        horiz ? b - a : T + 0.16, 0.16, horiz ? T + 0.16 : b - a, { tint: [0.86, 0.85, 0.82], density: 0.8 });
    }
    // pilastras a cada 8 m
    for (let u = s0 + 4; u < s1; u += 8) {
      kit.deco('concrete', horiz ? u : fixo, 1.85, horiz ? fixo : u,
        horiz ? 0.5 : T + 0.2, 3.7, horiz ? T + 0.2 : 0.5, { tint: [0.88, 0.87, 0.84], density: 0.7 });
    }
  }

  // Portão sul: dois pilares, viga e a folha corrediça recolhida.
  for (const s of [-1, 1]) {
    kit.solid('concrete', s * 5.0, 2.4, MURO, 1.0, 4.8, 1.0, { tint: [0.9, 0.89, 0.86], density: 0.7 });
    kit.deco('concrete', s * 5.0, 4.95, MURO, 1.24, 0.3, 1.24, { tint: [0.84, 0.83, 0.8] });
  }
  kit.solid('gunMetal', 0, 4.5, MURO, 9.2, 0.5, MURO_T, { density: 1.6, tint: [1.0, 1.02, 1.0] });
  kit.deco('metalPainted', -7.4, 1.5, MURO - 0.4, 5.0, 3.0, 0.12, { density: 1.0, tint: [0.92, 0.9, 0.82] });
  // poste de luz do portão: é o que dá referência de escala na entrada
  kit.pole(2.5, 31.4, 5.6, { surface: 'gunMetal', lamp: true, tint: [1.0, 1.02, 1.05], wires: [[-6.0, 4.6, 26.0]] });

  // Placa caída na brecha oeste: entrada de flanco que o muro não fecha.
  kit.deco('concrete', -MURO + 1.1, 0.3, 9.0, 2.4, 0.6, 3.6, { rotY: 0.22, density: 0.6, tint: [0.82, 0.81, 0.78] });
}

// ---------------------------------------------------------------------------
// casa de máquinas — o galpão alto a noroeste
// ---------------------------------------------------------------------------

function casaDeMaquinas(kit) {
  const M = 'metalPainted';
  const { W, E, N, S, H, TETO } = CASA;
  const t = 0.42;
  const tint = [1.05, 1.06, 1.04];
  const parede = { tint, ribs: 1.25, plinth: true, trim: 0 };

  // fachada leste: portão de carga, porta de gente e o vão alto da passarela
  kit.wall(M, E, N, E, S, 0, H, t, {
    ...parede,
    openings: [
      { u0: -16.0, u1: -11.4, y0: 0, y1: 4.6, frame: false },              // portão de carga
      { u0: -22.4, u1: -21.0, y0: 0, y1: 2.3 },                            // porta de serviço
      { u0: -12.3, u1: -9.9, y0: N1 - 0.2, y1: N1 + 2.1, frame: false },   // chegada da passarela
      { u0: -19.0, u1: -17.0, y0: 5.4, y1: 6.6, frame: false },
    ],
  });
  kit.wall(M, W, N, W, S, 0, H, t, {
    ...parede,
    openings: [
      { u0: -21.0, u1: -19.0, y0: 5.4, y1: 6.6, frame: false },
      { u0: -14.0, u1: -12.0, y0: 5.4, y1: 6.6, frame: false },
      { u0: -17.0, u1: -15.6, y0: 0, y1: 2.3 },                            // porta oeste
    ],
  });
  kit.wall(M, W, N, E, N, 0, H, t, {
    ...parede,
    openings: [
      { u0: -21.2, u1: -16.8, y0: 3.2, y1: 6.0, frame: false },            // boca do duto de gás
      { u0: -25.0, u1: -23.0, y0: 5.4, y1: 6.6, frame: false },
      { u0: -13.0, u1: -11.0, y0: 5.4, y1: 6.6, frame: false },
    ],
  });
  kit.wall(M, W, S, E, S, 0, H, t, {
    ...parede,
    openings: [
      { u0: -22.0, u1: -18.6, y0: 0, y1: 4.0, frame: false },              // portão sul
      { u0: -15.0, u1: -13.0, y0: 5.4, y1: 6.6, frame: false },
      { u0: -11.6, u1: -9.6, y0: 5.4, y1: 6.6, frame: false },
    ],
  });

  // Laje de cobertura, pisável, com acesso por escada externa (ver rotaAlta).
  // A laje avança sobre o topo das paredes: sem isso o topo de cada parede vira
  // uma faixa pisável de 18 m que ninguém alcança, e o teste de rota reclama.
  kit.slab('concrete', W - 0.3, E + 0.3, N - 0.3, S + 0.3, TETO, 0.4, [], { tint: [0.92, 0.92, 0.9] });
  // O vão do parapeito a leste é a chegada da ponte da escada S2 — sem ele a
  // ponte esbarraria em 80 cm de mureta no último passo.
  kit.parapet('concrete', W - 0.1, E + 0.1, N - 0.1, S + 0.1, TETO, 0.8, 0.28,
    [['e', -19.9, -17.7], ['s', -20.0, -18.0], ['n', -16.0, -14.0]]);

  // Caldeira: o volume que dá altura à silhueta. Topo a 13,8 m — acima dos 12 m
  // que o teste de rota trata como piso, então é volume, não laje órfã.
  kit.solid(M, -20.0, TETO + 3.1, -18.0, 8.4, 6.2, 7.6, { density: 0.55, tint: [1.0, 0.98, 0.94] });
  for (let z = -21.2; z < -14.6; z += 1.3) {
    kit.deco('rust', -20.0, TETO + 3.1, z, 8.6, 5.6, 0.18, { density: 1.4, tint: [0.95, 0.82, 0.72] });
  }
  kit.cylinder('rust', -15.2, TETO + 2.6, -18.0, 1.5, 5.2, { seg: 12, tint: [1.0, 0.86, 0.74] });
  kit.deco('gunMetal', -20.0, TETO + 6.5, -18.0, 8.8, 0.6, 8.0, { density: 0.9, tint: [1.0, 1.02, 1.04] });
  kit.vent(-24.4, TETO + 0.3, -11.6);
  kit.vent(-22.0, TETO + 0.3, -11.0);

  // Treliça do galpão, vista de dentro: é o que faz o vão ler como estrutura de
  // aço em vez de caixa de chapa.
  for (let z = N + 2.4; z < S; z += 3.2) {
    kit.deco('gunMetal', (W + E) * 0.5, H - 0.45, z, E - W - 0.6, 0.5, 0.28, { density: 1.2, tint: [1.0, 1.02, 1.04] });
    kit.deco('gunMetal', (W + E) * 0.5, H - 1.1, z, E - W - 1.6, 0.16, 0.16, { density: 2, tint: [0.95, 0.97, 1.0] });
  }
  for (const x of [W + 0.5, E - 0.5]) {
    for (let z = N + 2.4; z < S; z += 3.2) {
      kit.deco('gunMetal', x, H * 0.5, z, 0.34, H, 0.34, { density: 1.6, tint: [1.0, 1.02, 1.04] });
    }
  }
  // ponte rolante parada no meio do vão
  kit.deco('metalPainted', (W + E) * 0.5, H - 1.9, -16.2, E - W - 1.0, 0.8, 1.1, { density: 1.0, tint: [1.0, 0.95, 0.7] });
  kit.deco('gunMetal', -17.0, H - 2.6, -16.2, 1.6, 0.9, 1.6, { density: 1.4, tint: [0.9, 0.92, 0.95] });
  kit.cable([-17.0, H - 3.0, -16.2], [-17.0, 1.2, -16.2], 0.0, 0.04);

  // piso interno de concreto queimado de óleo
  kit.deco('concrete', (W + E) * 0.5, 0.03, (N + S) * 0.5, E - W - 0.8, 0.02, S - N - 0.8,
    { density: 0.7, tint: [0.88, 0.88, 0.86] });

  // --- as duas turbinas: os blocos que fazem o vão jogar --------------------
  // Base de concreto de 0,9 m (cobertura de pé) e carcaça em cima, topo a
  // 2,25 m. O degrau grande vira dois pequenos: chão -> base (0,9) -> carcaça
  // (mais 1,35), ambos dentro do mantle de 1,9 m. Sem a base, a carcaça seria
  // uma laje de 12 m² que ninguém alcança.
  for (const [x, z] of [[-21.5, -13.5], [-14.5, -20.0]]) {
    kit.solid('concrete', x, 0.45, z, 6.4, 0.9, 3.4, { density: 0.7, tint: [0.92, 0.91, 0.88] });
    kit.solid(M, x, 1.575, z, 5.0, 1.35, 2.4, { density: 0.9, tint: [1.0, 1.0, 0.98] });
    kit.cylinder('gunMetal', x - 2.9, 1.5, z, 0.75, 1.2, { seg: 12, tint: [0.95, 0.97, 1.0] });
    kit.cylinder('gunMetal', x + 2.9, 1.5, z, 0.6, 1.0, { seg: 12, tint: [0.95, 0.97, 1.0] });
    kit.deco('gunMetal', x, 2.4, z, 4.6, 0.3, 1.8, { density: 1.2, tint: [0.9, 0.92, 0.95] });
    kit.deco('rust', x + 1.4, 2.75, z, 0.5, 0.4, 0.5, { density: 1.6, tint: [1.0, 0.8, 0.65] });
  }

  // quadros elétricos no fundo: cobertura recortada de 1,8 m (escalável)
  for (let z = -23.6; z < -19.0; z += 1.6) {
    kit.solid(M, -25.4, 0.9, z, 1.1, 1.8, 1.4, { density: 1.2, tint: [0.94, 0.96, 0.94] });
    kit.deco('gunMetal', -24.8, 1.2, z, 0.08, 0.9, 1.0, { density: 2, tint: [0.8, 0.82, 0.84] });
  }
}

// ---------------------------------------------------------------------------
// chaminé — o marco visual, lido de qualquer canto do mapa
// ---------------------------------------------------------------------------

function chamine(kit) {
  const X = -18.0, Z = -30.2;
  // Três troncos de cone: a conicidade é o que separa chaminé de tubo.
  kit.cylinder('concrete', X, 4.5, Z, 3.0, 9.0, { rTop: 2.72, seg: 18, tint: [0.96, 0.95, 0.92] });
  kit.cylinder('concrete', X, 13.5, Z, 2.72, 9.0, { rTop: 2.44, seg: 18, tint: [0.92, 0.91, 0.88] });
  kit.cylinder('concrete', X, 21.9, Z, 2.44, 7.8, { rTop: 2.2, seg: 18, tint: [0.88, 0.87, 0.84] });
  kit.cylinder('rust', X, 26.0, Z, 2.26, 0.6, { seg: 18, tint: [1.0, 0.82, 0.68] });
  // Um colisor só, do chão ao topo. O topo a 25,8 m está acima dos 12 m que o
  // teste de rota considera piso: a chaminé é volume, não laje órfã.
  kit.colliderBox(X - 2.9, 0, Z - 2.9, X + 2.9, 25.8, Z + 2.9, 'concrete');
  // faixas de sinalização
  for (const y of [19.0, 22.4]) {
    kit.cylinder('rust', X, y, Z, 2.52, 1.4, { seg: 18, tint: [1.2, 0.7, 0.52] });
  }
  // escada de marinheiro com guarda-corpo: enfeite (7 cm não é degrau), mas é
  // o que explica a escala da chaminé quando se olha para cima
  for (let y = 1.2; y < 25.0; y += 0.42) {
    kit.deco('gunMetal', X + 3.0, y, Z, 0.1, 0.07, 0.7, { density: 2, tint: [0.8, 0.82, 0.84] });
  }
  for (const dz of [-0.36, 0.36]) {
    kit.deco('gunMetal', X + 3.1, 13.0, Z + dz, 0.08, 25.2, 0.08, { density: 2, tint: [0.78, 0.8, 0.82] });
  }
  // base alargada
  kit.deco('concrete', X, 0.55, Z, 7.4, 1.1, 7.4, { density: 0.7, tint: [0.9, 0.89, 0.86] });
}

// ---------------------------------------------------------------------------
// parque de tanques — nordeste e leste
// ---------------------------------------------------------------------------

/**
 * Tanque cilíndrico. Duas configurações, e a diferença entre elas é regra de
 * rota, não estética:
 *   - alto (h > 12): o topo fica acima da faixa que o teste de rota trata como
 *     piso, então é volume puro;
 *   - `perch:true`: tanque BAIXO cujo topo é posição de tiro, alcançável pela
 *     passarela do nível 2 com um degrau de 0,30 m.
 *
 * O colisor é o AABB do cilindro (é o que o kit faz, e é o certo para a cápsula
 * do jogador). No tanque-poleiro isso deixaria 1,3 m de piso invisível em cada
 * canto, então o topo ganha guarda-corpo quadrado inscrito no círculo, com vão
 * só onde a passarela chega.
 */
function tanque(kit, x, z, r, h, opt = {}) {
  const tint = opt.tint || [1.0, 0.9, 0.82];
  const sup = opt.surface || 'metalPainted';
  kit.cylinder(sup, x, h * 0.5, z, r, h, { seg: 20, tint });
  kit.colliderBox(x - r, 0, z - r, x + r, h, z + r, sup);
  // anéis de costura das chapas: sem eles o tanque lê como lata lisa
  for (let y = 1.9; y < h - 0.6; y += 1.9) {
    kit.cylinder('rust', x, y, z, r + 0.05, 0.16, { seg: 20, tint: [0.9, 0.78, 0.7] });
  }
  kit.cone(sup, x, h + 0.45, z, r + 0.12, 1.0, { seg: 20, tint });
  // escada helicoidal externa: enfeite, mas explica como se sobe no tanque
  const passos = Math.round((opt.voltas ?? 1.5) * 16);
  for (let i = 0; i < passos; i++) {
    const a = (i / 16) * Math.PI * 2 + (opt.faseEscada || 0);
    kit.deco('gunMetal', x + Math.cos(a) * (r + 0.35), (i / passos) * (h - 0.4) + 0.35, z + Math.sin(a) * (r + 0.35),
      0.9, 0.08, 0.9, { rotY: -a, density: 1.6, tint: [0.85, 0.87, 0.9] });
  }
  // bocal de topo e respiro
  kit.cylinder('gunMetal', x + r * 0.4, h + 1.1, z, 0.3, 0.7, { seg: 10, tint: [0.9, 0.92, 0.95] });
}

function tanques(kit) {
  // Os dois grandes do nordeste: 13 m de altura, silhueta do fundo do mapa.
  tanque(kit, 18.0, -25.0, 5.0, 13.2, { tint: [1.95, 1.88, 1.74] });
  tanque(kit, 29.2, -24.0, 4.4, 12.4, { tint: [1.82, 1.78, 1.68], faseEscada: 2.2 });
  // O do meio do pátio: é ele que corta a linha de tiro de 40 m do eixo do mapa.
  tanque(kit, 9.5, -3.0, 3.0, 12.6, { tint: [1.88, 1.8, 1.66], faseEscada: 1.1 });

  // O baixo do leste: topo pisável a 7,9 m, o poleiro que domina o pátio.
  tanque(kit, 28.5, -6.0, 4.4, PERCH, { tint: [2.0, 1.92, 1.78], faseEscada: 3.4, voltas: 1.0 });
  // Guarda-corpo do topo: quadrado inscrito no círculo. O lado OESTE tem vão de
  // z −4,8 para o sul — é por onde a passarela do nível 2 desemboca. Um lance
  // corrido aqui fecharia a única entrada do poleiro (corrimão é colisor).
  const q = 4.4 * 0.7;
  kit.rail(28.5 - q, -6 - q, 28.5 + q, -6 - q, PERCH);
  kit.rail(28.5 - q, -6 + q, 28.5 + q, -6 + q, PERCH);
  kit.rail(28.5 + q, -6 - q, 28.5 + q, -6 + q, PERCH);
  kit.rail(28.5 - q, -6 - q, 28.5 - q, -4.8, PERCH);

  // Bacia de contenção dos dois grandes: mureta de 1,1 m, cobertura de pé no
  // ponto mais aberto do mapa (e escalável, então não é laje órfã).
  for (const [ax, az, bx, bz] of [[11.6, -31.4, 25.0, -31.4], [11.6, -18.6, 25.0, -18.6], [11.6, -31.4, 11.6, -18.6]]) {
    const horiz = Math.abs(bx - ax) >= Math.abs(bz - az);
    kit.solid('concrete', (ax + bx) * 0.5, 0.55, (az + bz) * 0.5,
      horiz ? Math.abs(bx - ax) : 0.45, 1.1, horiz ? 0.45 : Math.abs(bz - az),
      { density: 0.8, tint: [0.92, 0.91, 0.88] });
  }
}

// ---------------------------------------------------------------------------
// pátio de transformadores — sudeste, cercado
// ---------------------------------------------------------------------------

function transformadores(kit) {
  // cerca de tela com portão a oeste e ao norte
  kit.fence(13.0, 8.0, 31.0, 8.0, 2.4, [[19.0, 22.0]], { gate: true });
  kit.fence(13.0, 8.0, 13.0, 28.0, 2.4, [[16.0, 19.0]], { gate: true });
  kit.fence(31.0, 8.0, 31.0, 28.0, 2.4, []);
  kit.fence(13.0, 28.0, 31.0, 28.0, 2.4, []);

  // Quatro unidades. Base de 0,4 m e corpo de 1,85 m: o topo (2,25 m) se
  // alcança subindo na base, então nenhuma delas vira laje órfã.
  for (const [x, z] of [[17.5, 12.5], [24.5, 12.5], [17.5, 20.5], [24.5, 20.5]]) {
    kit.solid('concrete', x, 0.2, z, 4.4, 0.4, 3.8, { density: 0.9, tint: [0.92, 0.91, 0.88] });
    kit.solid('metalPainted', x, 1.325, z, 3.0, 1.85, 2.6, { density: 0.9, tint: [0.88, 0.92, 0.9] });
    for (const s of [-1, 1]) {
      for (let i = -2; i <= 2; i++) {
        kit.deco('metalPainted', x + s * 1.65, 1.3, z + i * 0.45, 0.5, 1.6, 0.14,
          { density: 1.6, tint: [0.82, 0.86, 0.84] });
      }
    }
    for (const dx of [-0.9, 0, 0.9]) {
      kit.cylinder('plaster', x + dx, 2.65, z, 0.22, 0.9, { seg: 10, tint: [1.15, 1.12, 1.05] });
      kit.deco('gunMetal', x + dx, 3.14, z, 0.16, 0.12, 0.16, { density: 2 });
    }
  }

  // Casa de comando, com laje pisável a 3,2 m.
  const CX0 = 19.0, CX1 = 22.6, CZ0 = 13.4, CZ1 = 17.2;
  kit.wall('plaster', CX0, CZ0, CX1, CZ0, 0, 2.9, 0.25,
    { openings: [{ u0: 20.2, u1: 21.6, y0: 0, y1: 2.2 }], tint: [1.02, 1.02, 1.0], trim: 0.14 });
  kit.wall('plaster', CX0, CZ1, CX1, CZ1, 0, 2.9, 0.25,
    { openings: [{ u0: 19.8, u1: 21.4, y0: 1.0, y1: 2.3 }], tint: [1.02, 1.02, 1.0], trim: 0.14 });
  kit.wall('plaster', CX0, CZ0, CX0, CZ1, 0, 2.9, 0.25,
    { openings: [{ u0: 14.4, u1: 16.0, y0: 1.0, y1: 2.3 }], tint: [1.02, 1.02, 1.0], trim: 0.14 });
  kit.wall('plaster', CX1, CZ0, CX1, CZ1, 0, 2.9, 0.25, { tint: [1.02, 1.02, 1.0], trim: 0.14 });
  kit.slab('concrete', CX0 - 0.3, CX1 + 0.3, CZ0 - 0.3, CZ1 + 0.3, 3.2, 0.28, [], { tint: [0.92, 0.91, 0.88] });
  kit.parapet('concrete', CX0 - 0.3, CX1 + 0.3, CZ0 - 0.3, CZ1 + 0.3, 3.2, 0.7, 0.2, [['e', 14.5, 16.1]]);
  // Escada externa até a laje, INTEIRAMENTE a leste da laje: um lance que
  // entrasse por baixo do beiral travaria o jogador no meio, que é o defeito
  // clássico. O último degrau para em x 23,16 e a ponte cobre os 26 cm que
  // faltam até a borda.
  kit.stairs('gunMetal', 28.2, 15.3, -1, 0, 12, 0.26667, 0.42, 1.4, 0, { rail: 'both', tint: [0.92, 0.95, 0.97] });
  kit.catwalk('metalPainted', 22.6, 23.4, 14.5, 16.1, 3.2, { rails: [], thick: 0.22 });

  // pórticos de barramento: o que faz o pátio ler como subestação de longe
  for (const z of [10.4, 26.0]) {
    for (const x of [15.0, 21.0, 27.0]) {
      kit.solid('metalPainted', x, 2.6, z, 0.3, 5.2, 0.3, { density: 1.8, tint: [1.7, 1.74, 1.78] });
      kit.deco('plaster', x, 4.9, z, 0.16, 0.7, 0.16, { density: 2, tint: [1.1, 1.08, 1.02] });
    }
    kit.deco('gunMetal', 21.0, 5.3, z, 13.0, 0.24, 0.24, { density: 2, tint: [0.95, 0.97, 1.0] });
  }
  for (const dx of [-6, 0, 6]) kit.cable([21 + dx, 5.2, 10.4], [21 + dx, 5.2, 26.0], 1.4, 0.04);
}

// ---------------------------------------------------------------------------
// tubulação — o vocabulário que amarra as regiões
// ---------------------------------------------------------------------------

/**
 * Corrida de tubo sobre cavaletes. O tubo é caixa: o kit só faz cilindro no
 * eixo Y e o mapa não pode importar three para montar matriz de rotação. A
 * seção quadrada some atrás dos colares e da distância; o que importa é a
 * leitura de linha contínua atravessando o pátio.
 *
 * Corrida alta é DECO de propósito: um tubo sólido a 3 m com 0,5 m de seção
 * viraria uma laje pisável que ninguém alcança. Só os cavaletes são sólidos.
 */
function corrida(kit, ax, az, bx, bz, y, d, opt = {}) {
  const horiz = Math.abs(bx - ax) >= Math.abs(bz - az);
  const len = Math.hypot(bx - ax, bz - az);
  const cx = (ax + bx) * 0.5, cz = (az + bz) * 0.5;
  const tint = opt.tint || [0.95, 0.9, 0.85];
  const sup = opt.surface || 'rust';
  const sx = horiz ? len : d, sz = horiz ? d : len;
  if (opt.solid) kit.solid(sup, cx, y, cz, sx, d, sz, { density: 1.2, tint });
  else kit.deco(sup, cx, y, cz, sx, d, sz, { density: 1.2, tint });
  // colares a cada 3 m
  const n = Math.max(1, Math.round(len / 3));
  for (let i = 1; i < n; i++) {
    const t = i / n;
    kit.deco('gunMetal', kit.lerp(ax, bx, t), y, kit.lerp(az, bz, t),
      horiz ? 0.16 : d + 0.14, d + 0.14, horiz ? d + 0.14 : 0.16, { density: 2, tint: [0.85, 0.87, 0.9] });
  }
  if (opt.pes !== false) {
    const m = Math.max(1, Math.round(len / 6));
    for (let i = 0; i <= m; i++) {
      const t = i / m;
      kit.solid('gunMetal', kit.lerp(ax, bx, t), (y - d * 0.5) * 0.5, kit.lerp(az, bz, t),
        0.24, y - d * 0.5, 0.24, { density: 1.8, tint: [0.9, 0.92, 0.95] });
    }
  }
}

function tubulacao(kit) {
  // Duto de gás da casa de máquinas para a chaminé, a 4,6 m: apoiado nas duas
  // pontas, sem cavalete no meio (um pé ali nasceria dentro da chaminé).
  corrida(kit, -19.0, -25.0, -19.0, -27.8, 4.6, 2.6, { pes: false, tint: [0.92, 0.86, 0.8] });
  kit.deco('rust', -19.0, 4.6, -28.6, 2.2, 2.2, 2.2, { rotY: 0.4, density: 1.0, tint: [0.9, 0.82, 0.74] });
  for (const x of [-20.8, -17.2]) {
    kit.deco('gunMetal', x, 3.0, -26.4, 0.3, 3.2, 0.3, { density: 2, tint: [0.9, 0.92, 0.95] });
  }

  // Corredor de tubos do norte: casa de máquinas -> parque de tanques. Duas
  // corridas paralelas em alturas diferentes, que é como tubulação de usina se
  // parece de verdade.
  corrida(kit, -8.0, -27.6, 12.0, -27.6, 2.9, 0.55, { tint: [0.94, 0.86, 0.8] });
  corrida(kit, -8.0, -28.5, 12.0, -28.5, 2.25, 0.45, { tint: [0.92, 0.88, 0.82], pes: false });
  kit.cylinder('rust', 12.0, 1.45, -27.6, 0.42, 2.9, { seg: 12, tint: [0.94, 0.86, 0.8] });

  // Tubo baixo atravessando o pátio de norte a sul: obstáculo de 1,1 m que se
  // escala, e a peça que dá escala ao vazio do miolo. Este é sólido de
  // propósito — topo dentro do alcance do mantle, então é cobertura, não ilha.
  corrida(kit, 5.6, -14.0, 5.6, 6.0, 0.75, 0.7, { solid: true, tint: [0.92, 0.86, 0.8], pes: false });
  for (const z of [-11.0, -5.0, 1.0]) {
    kit.deco('gunMetal', 5.6, 0.2, z, 1.4, 0.4, 0.5, { density: 1.6, tint: [0.9, 0.92, 0.95] });
  }

  // Ramal para o pátio de transformadores, com a descida em cotovelo.
  corrida(kit, 14.0, -12.0, 30.0, -12.0, 3.1, 0.5, { tint: [0.92, 0.9, 0.86] });
  kit.cylinder('rust', 30.0, 1.55, -12.0, 0.32, 3.1, { seg: 12, tint: [0.92, 0.9, 0.86] });

  // Válvulas e volantes: o detalhe que lê de perto.
  for (const [x, z, y] of [[-2.0, -27.6, 2.9], [6.0, -27.6, 2.9], [22.0, -12.0, 3.1], [5.6, -8.0, 0.75]]) {
    kit.cylinder('gunMetal', x, y + 0.5, z, 0.42, 0.1, { seg: 12, tint: [1.1, 0.95, 0.7] });
    kit.deco('gunMetal', x, y + 0.25, z, 0.18, 0.5, 0.18, { density: 2, tint: [0.9, 0.92, 0.95] });
  }
}

// ---------------------------------------------------------------------------
// rota alta — as passarelas e as cinco escadas que abrem os três níveis
// ---------------------------------------------------------------------------

/**
 * ORDEM DE LEITURA DA ROTA (tudo a pé, de frente, sem pulo):
 *
 *   pátio (0)
 *     -> S1, 13 degraus de 0,277, sobe para o NORTE -> plataforma P1 (3,6)
 *          -> W1, para OESTE, entra na casa de máquinas pelo vão alto (3,6)
 *          -> E1, para LESTE, atravessa 22 m de pátio até o parque de tanques
 *          -> S2, 14 degraus de 0,286, para o NORTE -> ponte -> laje (7,6)
 *   E1 (3,6)
 *     -> S3, 14 degraus de 0,286, para o SUL -> passarela leste (7,6)
 *          -> degrau de 0,30 -> topo do tanque baixo (7,9)
 *   piso da casa de máquinas (0)
 *     -> S4, 13 degraus, para o NORTE -> passarela interna (3,6)
 *   pátio de transformadores (0)
 *     -> S5, 12 degraus, para o OESTE -> laje da casa de comando (3,2)
 *
 * NENHUM lance passa por baixo da laje que é o seu destino, e todos têm piso de
 * aproximação livre na base. Onde a passarela chega, o guarda-corpo ABRE — o
 * `rail` do kit é colisor, e um lance corrido fecharia a própria entrada.
 */
function rotaAlta(kit) {
  const M = 'metalPainted';
  const G = [0.92, 0.95, 0.97];

  // --- S1: pátio -> nível 1, em céu aberto a leste da casa de máquinas ------
  // Sobe para o NORTE. A base em z=−4,6 tem o pátio inteiro livre na frente.
  kit.stairs(M, -5.0, -4.6, 0, -1, 13, 0.27692, 0.42, 1.7, 0, { rail: 'both', tint: G });
  // Plataforma de chegada: SEM guarda-corpo, porque os quatro lados dela são
  // usados (S1 ao sul, S2 ao norte, W1 a oeste, E1 a leste).
  kit.catwalk(M, -6.0, -4.0, -12.3, -10.0, N1, { rails: [], thick: 0.22 });

  // --- W1: entra na casa de máquinas pelo vão alto da fachada leste ---------
  kit.catwalk(M, -9.6, -6.0, -12.2, -10.1, N1, { rails: ['n', 's'], thick: 0.22 });
  // passarela interna: um lance norte-sul junto à empena leste e um lance
  // leste-oeste no fundo
  kit.catwalk(M, -11.6, -9.6, -24.2, -10.1, N1, { rails: [], thick: 0.22 });
  kit.catwalk(M, -26.2, -11.6, -24.2, -22.2, N1, { rails: [], thick: 0.22 });
  // Guarda-corpo em lances, com os vãos exatamente onde se chega e se sai:
  // x=−11,6 abre entre z −19,6 e −17,0 (chegada de S4) e x=−9,6 abre entre
  // z −12,4 e −10,1 (chegada de W1).
  kit.rail(-11.6, -24.2, -11.6, -19.6, N1);
  kit.rail(-11.6, -17.0, -11.6, -12.4, N1);
  kit.rail(-9.6, -22.0, -9.6, -12.4, N1);
  kit.rail(-26.2, -22.2, -11.6, -22.2, N1);
  kit.rail(-26.2, -24.2, -26.2, -22.2, N1);

  // --- S4: escada interna piso -> passarela interna --------------------------
  // Sobe para o NORTE, 1,2 m afastada da passarela de destino: ela sobe AO LADO
  // do deck, não por baixo dele. O patamar no topo faz a ligação lateral.
  kit.stairs('gunMetal', -12.9, -12.0, 0, -1, 13, 0.27692, 0.40, 1.5, 0, { rail: 'both', tint: G });
  // Guarda-corpo a oeste e ao NORTE: os dois lados que morrem no vazio. O sul é
  // a chegada da escada e o leste é a emenda com a passarela — esses ficam
  // abertos. A borda norte estava sem nada e o jogador caía 3,6 m andando reto;
  // quem achou foi a prova de caminhada, não olho nenhum.
  kit.catwalk(M, -13.7, -11.6, -19.4, -17.2, N1, { rails: ['w', 'n'], thick: 0.22 });

  // --- E1: passarela do pátio, nível 1, oeste -> leste ----------------------
  kit.catwalk(M, -4.0, 7.0, -11.6, -10.2, N1, { rails: ['n', 's'], thick: 0.22 });
  // O segundo lance leva só o guarda-corpo NORTE pelo kit; o do sul é posto à
  // mão e para em x=16,2, deixando aberta a boca da escada S3.
  kit.catwalk(M, 7.0, 18.0, -11.6, -10.2, N1, { rails: ['n'], thick: 0.22 });
  kit.rail(7.0, -10.2, 16.2, -10.2, N1);
  kit.rail(18.0, -11.6, 18.0, -10.2, N1);
  // colunas de apoio: 22 m de passarela sem pé lê como prancha flutuando
  for (const x of [-1.0, 3.0, 7.0, 11.0, 15.0]) {
    kit.solid('metalPainted', x, 1.69, -10.9, 0.32, 3.38, 0.32, { density: 1.8, tint: [1.78, 1.82, 1.86] });
  }

  // --- S2: nível 1 -> laje da casa de máquinas (7,6) ------------------------
  // Continua para o NORTE a partir de P1, em céu aberto. A ponte de chegada
  // começa em z=−17,8, DEPOIS do último degrau (que para em −17,9): se ela
  // avançasse sobre o lance, a cabeça bateria no fundo dela no meio da subida.
  kit.stairs(M, -5.0, -12.3, 0, -1, 14, 0.28572, 0.40, 1.6, N1, { rail: 'both', tint: G });
  kit.catwalk(M, -8.9, -4.2, -19.8, -17.8, N2, { rails: ['n', 's'], thick: 0.22 });

  // --- S3: nível 1 -> passarela leste (7,6) -> topo do tanque (7,9) ---------
  // Sobe para o SUL a partir do fim de E1, longe de qualquer laje. A passarela
  // do topo começa em z=−4,6, exatamente onde o lance termina.
  kit.stairs(M, 17.2, -10.2, 0, 1, 14, 0.28572, 0.40, 1.6, N1, { rail: 'both', tint: G });
  kit.catwalk(M, 16.2, 24.4, -4.6, -2.8, N2, { rails: ['s'], thick: 0.22 });
  kit.rail(18.2, -4.6, 24.4, -4.6, N2);
  kit.rail(16.2, -4.6, 16.2, -2.8, N2);
  for (const x of [17.4, 23.6]) {
    kit.solid('metalPainted', x, 3.69, -3.4, 0.32, 7.38, 0.32, { density: 1.8, tint: [1.78, 1.82, 1.86] });
  }
}

// ---------------------------------------------------------------------------
// FASE 2 — espalhamento. Tudo aqui consulta groundAt()/isSolid() sobre a lista
// de colisores já fechada, então nada pode vir antes da geometria.
// ---------------------------------------------------------------------------

function espalhamento(kit, rng) {
  // postos improvisados nos pontos quentes, um por nível
  kit.emplacement({ x: 0.0, z: -20.0, yaw: 0.0, len: 4.0, y: 0 });
  kit.emplacement({ x: -8.0, z: 4.0, yaw: Math.PI / 2, len: 3.4, y: 0 });
  kit.emplacement({ x: 15.0, z: 2.0, yaw: 0.3, len: 3.6, y: 0 });
  kit.emplacement({ x: -16.0, z: -11.0, yaw: 0.0, len: 3.2, y: CASA.TETO });
  kit.emplacement({ x: 27.5, z: -6.6, yaw: 0.0, len: 3.0, y: PERCH });
  kit.sandbags();

  kit.crates([
    [-13.0, -6.0], [-12.0, -5.2], [2.0, -24.0], [3.0, -23.2], [13.0, -6.0],
    [24.0, -18.0], [25.0, -17.2], [-30.0, -6.0], [-29.0, -5.2], [-22.0, 12.0],
    [-8.0, 26.0], [-7.0, 26.9], [10.0, 18.0], [30.0, 4.0], [16.0, 31.0],
    [-2.0, 8.0], [-31.0, 20.0], [6.0, -32.0], [26.0, 24.0], [-16.0, -14.0],
  ]);

  kit.pallets({
    count: 18,
    area: [-32, 32, -32, 32],
    extra: [[-10.0, 0.02, -8.0, 0.3, 1], [-10.0, 0.16, -8.0, 0.7, 1], [14.6, 0.02, 6.0, 1.1, 1]],
  });

  kit.drums([
    [-7.6, -8.0], [-8.4, -8.7], [-7.2, -9.4], [4.0, 10.0], [4.8, 10.6],
    [12.5, -16.0], [13.2, -16.7], [-28.0, 14.0], [-27.2, 14.7], [22.0, -16.0],
    [29.5, 10.0], [-2.0, 20.0], [-14.0, 22.0], [8.0, -20.0], [8.8, -20.6],
    [26.0, -30.0], [-32.0, -14.0], [18.0, 5.0],
  ]);

  kit.tyres([[-30.0, 26.0, 4], [-28.6, 26.4, 3], [10.0, 12.0, 3], [30.0, -32.0, 4], [-4.0, 16.0, 2], [20.0, 31.0, 3]]);

  // Entulho concentrado onde a coisa quebrou: brecha do muro oeste, pé da
  // chaminé, portão sul da casa de máquinas e a boca da bacia dos tanques.
  const focos = [[-33.5, 9.0, 3.0], [-19.0, -27.0, 3.0], [-20.0, -8.6, 2.6], [17.0, -18.6, 2.4]];
  kit.rubbleField({ count: 190, hotspots: focos, limit: 34, tint: [0.98, 0.98, 0.96] });
  kit.bricks({ count: 90, hotspots: focos, limit: 34, tint: [0.74, 0.62, 0.54] });
  kit.litter({ count: 110, limit: 33, tint: [1.4, 1.42, 1.4] });

  // Mato nascendo nas juntas da laje: é o que data a usina como DESATIVADA.
  for (let i = 0; i < 26; i++) {
    const x = kit.rand(-32, 32), z = kit.rand(-32, 32);
    if (kit.groundAt(x, z) > 0.2) continue;
    kit.deco('fabric', x, 0.14, z, 0.5 + rng() * 1.6, 0.28, 0.4 + rng() * 1.2,
      { rotY: rng() * 3.14, density: 1.2, tint: [0.55, 0.62, 0.4] });
  }
}
